import { FC, useCallback, useMemo, useState } from 'react';
import { Column, CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';
import { getUserInfo } from 'utils/auth';
import { IResponseData } from 'types/api';
import { LeavesAvailabilityReportParams, LeavesAvailabilityReportRow } from '../types';

interface IProps {
  reportParams: LeavesAvailabilityReportParams;
}

const LeavesAvailabilityTable: FC<IProps> = ({ reportParams }) => {
  const { t } = useTranslation();
  const {
    featureLeaveDaysServedTime,
    featureLeaveDaysAdditionalLeave,
    featureLeaveDaysIrregularWorkingHours,
    featureLeaveDaysSpecificWorkingConditions,
  } = getUserInfo();
  const [yearColumns, setYearColumns] = useState<string[]>([]);

  const isYearKey = (key: string) => /^\d{4}$/.test(key);

  const formatDaysValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return Number.isInteger(value) ? value.toString() : value.toString();
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      const numericValue = Number(trimmed);
      if (Number.isNaN(numericValue)) return trimmed;
      if (Number.isInteger(numericValue)) return numericValue.toString();
      return trimmed.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0+$/u, '');
    }
    return String(value);
  };

  const getGroupPreview = (value?: string | null) => {
    if (!value) return { preview: '', full: '' };
    const groups = value
      .split(',')
      .map(group => group.trim())
      .filter(Boolean);
    if (groups.length <= 2) return { preview: groups.join(', '), full: groups.join(', ') };
    return { preview: `${groups.slice(0, 2).join(', ')}...`, full: groups.join(', ') };
  };

  const onDataFetched = useCallback((data: IResponseData<LeavesAvailabilityReportRow>) => {
    const years = new Set<string>();

    data.items.forEach(item => {
      Object.keys(item).forEach(key => {
        if (isYearKey(key)) years.add(key);
      });
    });

    const nextYears = [...years].sort();

    setYearColumns(prev => {
      if (prev.length === nextYears.length && prev.every((year, index) => year === nextYears[index])) {
        return prev;
      }
      return nextYears;
    });
  }, []);

  const columns: Array<Column<LeavesAvailabilityReportRow>> = useMemo(() => {
    const baseColumns: Array<Column<LeavesAvailabilityReportRow>> = [
      {
        Header: 'ID',
        accessor: 'id',
        disableSortBy: true,
      },
      {
        Header: t('reports.firstName') || '',
        accessor: 'first_name',
        disableSortBy: true,
      },
      {
        Header: t('reports.lastName') || '',
        accessor: 'last_name',
        disableSortBy: true,
      },
      {
        Header: t('reports.position') || '',
        accessor: 'position',
        disableSortBy: true,
      },
      {
        Header: t('reports.group') || '',
        accessor: 'group',
        disableSortBy: true,
        Cell: ({ row }: CellProps<LeavesAvailabilityReportRow>) => {
          const { preview, full } = getGroupPreview(row.original.group);
          if (!full) return <span />;
          return (
            <TooltipWrapper
              id={`tooltip-group-${row.original.id}`}
              overlay={full}
            >
              <span>{preview}</span>
            </TooltipWrapper>
          );
        },
      },
      {
        Header: t('reports.status') || '',
        accessor: 'status',
        disableSortBy: true,
      },
    ];

    const yearCols: Array<Column<LeavesAvailabilityReportRow>> = yearColumns.map(year => ({
      Header: year,
      accessor: year,
      disableSortBy: true,
      Cell: ({ value }: CellProps<LeavesAvailabilityReportRow>) => <span>{formatDaysValue(value)}</span>,
    }));

    const featureColumns: Array<Column<LeavesAvailabilityReportRow>> = [];

    if (featureLeaveDaysServedTime) {
      featureColumns.push({
        Header: t('reports.leavesAvailabilityServedTime') || '',
        accessor: 'paid_leaves_served_time',
        disableSortBy: true,
        Cell: ({ value }: CellProps<LeavesAvailabilityReportRow>) => <span>{formatDaysValue(value)}</span>,
      });
    }

    if (featureLeaveDaysAdditionalLeave) {
      featureColumns.push({
        Header: t('reports.leavesAvailabilityAdditional') || '',
        accessor: 'paid_leaves_additional',
        disableSortBy: true,
        Cell: ({ value }: CellProps<LeavesAvailabilityReportRow>) => <span>{formatDaysValue(value)}</span>,
      });
    }

    if (featureLeaveDaysIrregularWorkingHours) {
      featureColumns.push({
        Header: t('reports.leavesAvailabilityIrregularWorkingHours') || '',
        accessor: 'paid_leaves_irregular_working_hours',
        disableSortBy: true,
        Cell: ({ value }: CellProps<LeavesAvailabilityReportRow>) => <span>{formatDaysValue(value)}</span>,
      });
    }

    if (featureLeaveDaysSpecificWorkingConditions) {
      featureColumns.push({
        Header: t('reports.leavesAvailabilitySpecificWorkingConditions') || '',
        accessor: 'paid_leaves_specific_working_conditions',
        disableSortBy: true,
        Cell: ({ value }: CellProps<LeavesAvailabilityReportRow>) => <span>{formatDaysValue(value)}</span>,
      });
    }

    return [...baseColumns, ...yearCols, ...featureColumns];
  }, [
    t,
    yearColumns,
    featureLeaveDaysServedTime,
    featureLeaveDaysAdditionalLeave,
    featureLeaveDaysIrregularWorkingHours,
    featureLeaveDaysSpecificWorkingConditions,
  ]);

  return (
    <FetchTable<LeavesAvailabilityReportRow>
      columns={columns}
      entityName="leaves-availability-report"
      entityEndpoint="reports/employee/paid-leaves-count/json"
      requestType="POST"
      searchParams={reportParams}
      pageLimit={20}
      skipResetExpanded
      hideFilterBar
      isReport
      pagination
      onDataFetched={onDataFetched}
    />
  );
};

export default LeavesAvailabilityTable;
